import "../_namespaces.js";
import "./reselector/provider_selector.js";
import "./reselector/suggestion_selector.js";

window.AppointmentBooking ??= {};
window.AppointmentBooking.SlotSuggester ??= {};
window.AppointmentBooking.SlotSuggester.Views ??= {};

// Preserve sub-view registrations (e.g. ProviderSelector, SuggestionSelector)
// that depth-first imports already placed on the Reselector namespace object.
const __ReselectorPrior = AppointmentBooking.SlotSuggester.Views.Reselector;

AppointmentBooking.SlotSuggester.Views.Reselector = class Reselector extends (
  AppointmentBooking.SlotSuggester.Views.Base
) {
  constructor() {
    super(...arguments);
    this.attributes = this.attributes.bind(this);
    this.initialize = this.initialize.bind(this);
    this.handleSlotPick = this.handleSlotPick.bind(this);
    this.render = this.render.bind(this);
    this.contextForRender = this.contextForRender.bind(this);
    this.calendarResponseProcessor = this.calendarResponseProcessor.bind(this);
    this.addReferencedCalendarSlots = this.addReferencedCalendarSlots.bind(this);
    this.addReferencedCalendarProviders = this.addReferencedCalendarProviders.bind(this);
    this.assignProviderToSlot = this.assignProviderToSlot.bind(this);
    this.assignAddressToSlot = this.assignAddressToSlot.bind(this);
  }

  attributes() {
    return { class: `appointment-booking-slot-reselector --variant-${this.model.get("variant")}` };
  }

  initialize() {
    super.initialize(...arguments);
    this.providerSelector = new this.Views.ProviderSelector({ model: this.model });
    this.suggestionSelector = new this.Views.SuggestionSelector({ model: this.model });

    this.confirmationModalModel = new this.Models.ConfirmationModal();
    this.confirmationModal = new this.Views.SuggestionSelector.ConfirmationModal({
      model: this.confirmationModalModel,
      appendTo: this.el,
    });

    // BEHAVIOUR

    this.listenTo(this.model, "change:selectedDate", this.render);
    return this.listenTo(this.model, "reset", this.render);
  }

  // EVENT HANDLERS

  handleSlotClick(event) {
    return false; // Swallow slot clicks from the calendar so our parent view doesn't respond to them and apply a date we're simply looking at slots for
  }

  handleSlotConfirm(event, slotId) {
    return this.model.set("selectedSlotId", slotId);
  }

  handleSelectedDateClick(event) {
    event.preventDefault();

    const filters = jQuery.extend(
      { selectedProviderOption: this.model.get("selectedProviderOption") },
      this.model.get("desiredSlotConditions"),
    );
    filters.departmentId = event.currentTarget.dataset.departmentId;

    return AppointmentBooking.showSlotPickerModal({
      focusDate: this.selectedDate,
      filters: Sparkle.Utilities.camelToSnakeObject(filters),
      options: {
        appendTo: this.el,
        list: false,
        header: {
          title: this.I18n().slot_picker_modal.title,
        },
        calendar: {
          api: "slot_suggester_modal_calendar",
          processor: this.calendarResponseProcessor,
        },
        resource: false,
        renderLinks: false,
        availabilitySelector: false,
        patientTypeSelector: false,
        addressSelector: false,
        analyticsScope: "booking",
        onSlotPick: (pickedTimeslot, slotElement, modal) => {
          modal.hide(); // Close the slot picker modal immediately so it doesn't overlap with the confirmation modal
          return this.handleSlotPick(pickedTimeslot);
        },
      },
    });
  }

  handleSlotPick(pickedTimeslot) {
    const response = Sparkle.Utilities.snakeToCamelObject(pickedTimeslot.toJSON());

    // Show confirmation modal
    this.confirmationModalModel.set({
      selectedDate: response.startTime,
      slots: new this.Collections.ConfirmationModalSlots(response.slots),
    });
    return this.confirmationModal.show();
  }

  // RENDERING

  render() {
    super.render(...arguments);
    this.replaceContents(".provider-selector-container", this.providerSelector.el);
    this.replaceContents(".suggestion-selector-container", this.suggestionSelector.el);

    return this;
  }

  contextForRender() {
    let desiredLocationSlotCount;
    let otherLocationSlotCount;
    const context = super.contextForRender(...arguments);
    context.showProviderSelector = !!this.poolProviders.length;

    if (this.desiredProvider) {
      context.desiredProvider = this.desiredProvider.toJSON();
    }

    if (
      context.suggestionAlgorithmKey === "with_any_provider_location" ||
      context.suggestionAlgorithmKey === "with_provider_location"
    ) {
      context.showTitle = false;
      context.showProviderSelector = false;
    } else {
      context.showTitle = true;
    }

    if (this.model.get("desiredProviderSlots").length) {
      context.providerMessageKey = "same_provider";
      desiredLocationSlotCount = this.model
        .get("desiredProviderSlots")
        .where({ departmentId: this.model.get("desiredDepartmentId") }).length;
      otherLocationSlotCount = this.model.get("desiredProviderSlots").length - desiredLocationSlotCount;
    } else {
      context.providerMessageKey = "new_provider";
      desiredLocationSlotCount = this.model
        .get("poolSlots")
        .where({ departmentId: this.model.get("desiredDepartmentId") }).length;
      otherLocationSlotCount = this.model.get("poolSlots").length - desiredLocationSlotCount;
    }

    if (context.suggestionAlgorithmKey === "with_any_provider_location") {
      context.locationMessageKey = "any_provider_location";
    } else if (context.suggestionAlgorithmKey === "with_provider_location") {
      context.locationMessageKey = "provider_location";
    } else if (desiredLocationSlotCount > 0 && otherLocationSlotCount > 0) {
      context.locationMessageKey = "same_and_other_locations";
    } else if (desiredLocationSlotCount > 0) {
      context.locationMessageKey = "same_location";
    } else if (otherLocationSlotCount > 0) {
      context.locationMessageKey = "other_location";
    }

    return context;
  }

  // HELPER METHODS

  calendarResponseProcessor(response) {
    response.metadata = Sparkle.Utilities.snakeToCamelObject(response.metadata);
    this.addReferencedCalendarProviders(response.metadata.referencedProviders);
    this.addReferencedCalendarSlots(response.data, response.metadata);

    return response.data;
  }

  addReferencedCalendarSlots(data, metadata) {
    const suggestedSlots = this.model.get("suggestedSlots");

    return data.forEach((date) =>
      date.slots.forEach(
        (
          stack, // Calendar calls these stacks 'slots' because it doesn't know they represent several slots
        ) =>
          stack.slots.forEach((slot) => {
            // But inside we've added the list of slots so the confirmation modal can show a choice of slots
            slot = Sparkle.Utilities.snakeToCamelObject(slot);
            this.assignProviderToSlot(slot);
            this.assignAddressToSlot(slot, metadata);
            return suggestedSlots.add(slot);
          }),
      ),
    );
  }

  addReferencedCalendarProviders(providers) {
    return this.model.get("availableProviders").add(providers);
  }

  assignProviderToSlot(slotJSON) {
    const provider = this.assert.presence(
      this.model.get("availableProviders").findWhere({ id: slotJSON.providerId, type: slotJSON.providerType }),
      `Can't find provider for slot ${slotJSON.id}`,
    );
    return (slotJSON.provider = provider);
  }

  assignAddressToSlot(slotJSON, metadata) {
    return (slotJSON.address = metadata.referencedAddresses.find((address) => address.id === slotJSON.addressId));
  }
};

AppointmentBooking.SlotSuggester.Views.Reselector.prototype.tagName = "div";
AppointmentBooking.SlotSuggester.Views.Reselector.prototype.template = "reselector";

AppointmentBooking.SlotSuggester.Views.Reselector.prototype.events = {
  "click [data-behaviour=date-select]": "handleSelectedDateClick",
  "click [data-slot-id]": "handleSlotClick",
  [AppointmentBooking.SlotSuggester.Events.slot.confirm]: "handleSlotConfirm",
};

AppointmentBooking.SlotSuggester.Views.Reselector.get("selectedDate", function () {
  return this.model.get("selectedDate");
});

AppointmentBooking.SlotSuggester.Views.Reselector.get("suggestedSlots", function () {
  return this.model.get("suggestedSlots");
});

AppointmentBooking.SlotSuggester.Views.Reselector.get("desiredProvider", function () {
  return this.model.get("desiredProvider");
});

AppointmentBooking.SlotSuggester.Views.Reselector.get("poolProviders", function () {
  return this.model.get("poolProviders");
});

AppointmentBooking.SlotSuggester.Views.Reselector.get("selectedProviderOption", function () {
  return this.model.get("selectedProviderOption");
});

AppointmentBooking.SlotSuggester.Views.Reselector.get("desiredSlotConditions", function () {
  return this.model.get("desiredSlotConditions");
});

if (__ReselectorPrior) {
  Object.assign(AppointmentBooking.SlotSuggester.Views.Reselector, __ReselectorPrior);
}
